import { useEffect, useState } from 'react'
import { useHubLang } from '../lib/i18n'
import { COMPOSE } from '../lib/hubCopy'
import {
  decideSuggestion,
  removePhotoTag,
  withdrawSuggestion,
  type PhotoTag,
  type SuggestRights,
  type TagSuggestion,
} from '../lib/photoTags'
import { PeopleIcon } from './icons'

/* 161: the composer's tagger is PhotoTagEditor now — the tag goes on the
   person in the photo. This file keeps the viewer's line. */

/**
 * 143. Who is in the photo, under the caption in the viewer.
 *
 * If you are one of them: "Remove my tag", and a line saying how to ask the
 * company not to use the photo. 170 (David, 11 Oct) took away the box that
 * used to sit here: "it should not be a toggle check box". Not wanting it used
 * is a review request with "I'm in this photo" (PhotoReviewButton, under the
 * photo), open for 30 days after you are tagged. Removing the tag is still
 * separate, and on its own stops nothing.
 */
export function PhotoTagsLine({
  path,
  tags,
  onChanged,
  onFocusPerson,
}: {
  path: string
  tags: PhotoTag[]
  onChanged: () => void
  /** 161. Hovering or focusing a name lights up that person's pin. */
  onFocusPerson?: (personId: string | null) => void
}) {
  const t = COMPOSE[useHubLang()]
  const mine = tags.find((x) => x.isMe) ?? null
  const others = tags
  const [removedMine, setRemovedMine] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A different photo, a fresh line.
  useEffect(() => {
    setRemovedMine(false)
    setError(null)
  }, [path])

  async function run(f: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await f()
    } catch {
      setError(t.tagFailed)
    } finally {
      setBusy(false)
    }
  }

  const showMine = mine !== null || removedMine
  if (others.length === 0 && !showMine) return null

  return (
    <div className="lightbox__tags">
      {others.length > 0 && (
        <p className="lightbox__with">
          <span>{t.taggedWith} </span>
          {others.map((x, i) => (
            <span
              key={x.personId}
              className={`lightbox__tagged${x.x !== null ? ' lightbox__tagged--placed' : ''}`}
              tabIndex={x.x !== null ? 0 : undefined}
              onMouseEnter={() => onFocusPerson?.(x.personId)}
              onMouseLeave={() => onFocusPerson?.(null)}
              onFocus={() => onFocusPerson?.(x.personId)}
              onBlur={() => onFocusPerson?.(null)}
            >
              <span className="lightbox__tagname">{x.displayName}</span>
              {x.canRemove && !x.isMe && (
                <button
                  type="button"
                  className="lightbox__untag"
                  disabled={busy}
                  aria-label={t.tagRemove(x.displayName)}
                  onClick={() => void run(async () => {
                    await removePhotoTag(path, x.personId)
                    onChanged()
                  })}
                >
                  ×
                </button>
              )}
              {i < others.length - 1 ? ', ' : ''}
            </span>
          ))}
        </p>
      )}
      {showMine && (
        <div className="lightbox__mine">
          <p>{removedMine ? t.myTagRemoved : t.youAreTagged}</p>
          {!removedMine && (
            <button
              type="button"
              className="btn btn--xs"
              disabled={busy}
              onClick={() => void run(async () => {
                await removePhotoTag(path)
                setRemovedMine(true)
                onChanged()
              })}
            >
              {t.removeMyTag}
            </button>
          )}
          <p className="lightbox__note">{t.myReviewNote}</p>
        </div>
      )}
      {error && <p className="lightbox__note" role="alert">{error}</p>}
    </div>
  )
}

/**
 * 163. Under the viewer's tag line: the suggestions waiting on this photo that
 * you may see, and "Suggest a tag" if you may suggest. Whoever decides (the
 * poster, a moderator, a gallery admin) sees each with who suggested it and
 * Accept / Decline; a suggester sees their own, with Withdraw. The person
 * suggested sees nothing here until it is accepted — the server decides that.
 */
export function PhotoSuggestionsLine({
  suggestions,
  rights,
  onSuggest,
  onChanged,
  onFocusPerson,
}: {
  suggestions: TagSuggestion[]
  rights: SuggestRights | null
  onSuggest: () => void
  onChanged: () => void
  onFocusPerson?: (id: string | null) => void
}) {
  const t = COMPOSE[useHubLang()]
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const deciding = suggestions.filter((s) => s.canDecide)
  const mine = suggestions.filter((s) => s.isMine && !s.canDecide)

  async function run(f: () => Promise<string | boolean>) {
    setBusy(true)
    setNote(null)
    try {
      const out = await f()
      if (typeof out === 'string' && out !== 'accepted' && out !== 'declined') setNote(t.suggestionClosed)
      onChanged()
    } catch {
      setNote(t.tagFailed)
    } finally {
      setBusy(false)
    }
  }

  if (deciding.length === 0 && mine.length === 0 && !rights?.maySuggest && !note) return null

  return (
    <div className="lightbox__suggest">
      {deciding.length > 0 && (
        <>
          <p className="lightbox__note">{t.suggestionsWaiting(deciding.length)}</p>
          <ul className="lightbox__suggestions">
            {deciding.map((s) => (
              <li
                key={s.id}
                onMouseEnter={() => onFocusPerson?.(s.id)}
                onMouseLeave={() => onFocusPerson?.(null)}
              >
                <span className="lightbox__suggested">{s.displayName}</span>
                <span className="lightbox__by">{t.suggestedBy(s.suggestedByName)}</span>
                <button
                  type="button"
                  className="btn btn--xs"
                  disabled={busy}
                  aria-label={t.acceptSuggestion(s.displayName)}
                  onClick={() => void run(() => decideSuggestion(s.id, true))}
                >
                  {t.accept}
                </button>
                <button
                  type="button"
                  className="btn btn--xs btn--quiet"
                  disabled={busy}
                  aria-label={t.declineSuggestion(s.displayName)}
                  onClick={() => void run(() => decideSuggestion(s.id, false))}
                >
                  {t.decline}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {mine.map((s) => (
        <p key={s.id} className="lightbox__note">
          {t.yourSuggestion(s.displayName)}{' '}
          <button type="button" className="btn btn--link" disabled={busy} onClick={() => void run(() => withdrawSuggestion(s.id))}>
            {t.withdraw}
          </button>
        </p>
      ))}
      {rights?.maySuggest && (
        <button type="button" className="btn btn--xs lightbox__suggestbtn" onClick={onSuggest} disabled={busy}>
          <PeopleIcon size={14} />
          {t.suggestTag}
        </button>
      )}
      {note && <p className="lightbox__note" role="status">{note}</p>}
    </div>
  )
}
